import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import * as LucideIcons from "lucide-react";
import { BookOpen, Search, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/contexts/I18nContext";

// Batch 35 · B35-1: a section that carries Arabic text (title_ar / subtitle_ar / body_ar — the sections added in manual 2.5)
// is shown in Arabic when the interface is in Arabic; every other section stays in English.
export function localised(s, lang) {
  if (!s || lang !== "ar" || !s.body_ar) return s;
  return { ...s, title: s.title_ar || s.title, subtitle: s.subtitle_ar || s.subtitle, body: s.body_ar };
}

function IconFor({ name, className = "h-4 w-4" }) {
  const Cmp = LucideIcons[name] || LucideIcons.FileText;
  return <Cmp className={className} />;
}

function Highlight({ text, q }) {
  if (!q) return <>{text}</>;
  try {
    const parts = String(text).split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
    return parts.map((p, i) =>
      p.toLowerCase() === q.toLowerCase()
        ? <mark key={i} className="rounded bg-yellow-200 px-0.5">{p}</mark>
        : <React.Fragment key={i}>{p}</React.Fragment>
    );
  } catch { return <>{text}</>; }
}

function Block({ b, q }) {
  if (b.kind === "p") return <p className="mb-3 text-sm leading-relaxed text-slate-700 dark:text-slate-300"><Highlight text={b.text} q={q} /></p>;
  if (b.kind === "h") return <h3 className="mt-6 mb-2 text-base font-semibold text-emerald-600 dark:text-emerald-400"><Highlight text={b.text} q={q} /></h3>;
  if (b.kind === "steps") return (
    <ol className="mb-4 list-decimal space-y-1.5 ps-6 text-sm text-slate-700 dark:text-slate-300">
      {b.items.map((it, i) => <li key={i}><Highlight text={it} q={q} /></li>)}
    </ol>
  );
  if (b.kind === "bullets") return (
    <ul className="mb-4 list-disc space-y-1 ps-6 text-sm text-slate-700 dark:text-slate-300">
      {b.items.map((it, i) => <li key={i}><Highlight text={it} q={q} /></li>)}
    </ul>
  );
  if (b.kind === "code") return (
    <pre className="mb-4 overflow-x-auto rounded-md border border-slate-300 bg-slate-50 p-3 text-[11.5px] leading-relaxed text-slate-800 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-200"
         data-testid="help-code">
      <code className="whitespace-pre font-mono">{b.text}</code>
    </pre>
  );
  if (b.kind === "table") return (
    <div className="mb-4 overflow-x-auto">
      <table className="w-full border text-sm">
        <thead className="bg-emerald-600 text-white">
          <tr>{b.header.map((h, i) => <th key={i} className="p-2 text-start text-xs uppercase">{h}</th>)}</tr>
        </thead>
        <tbody>
          {b.rows.map((r, i) => (
            <tr key={i} className={i % 2 ? "bg-slate-50 dark:bg-slate-900/40" : ""}>
              {r.map((c, j) => <td key={j} className="border-t p-2 align-top"><Highlight text={c} q={q} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  if (b.kind === "tip") return (
    <div className="mb-4 rounded border border-emerald-400 bg-emerald-50 p-3 text-sm text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
      <b>Tip.</b> <Highlight text={b.text} q={q} />
    </div>
  );
  if (b.kind === "warn") return (
    <div className="mb-4 rounded border border-red-400 bg-red-50 p-3 text-sm text-red-900 dark:bg-red-950/40 dark:text-red-200">
      <b>Warning.</b> <Highlight text={b.text} q={q} />
    </div>
  );
  return null;
}

export default function HelpCenter() {
  const { lang } = useI18n() || {};
  const [raw, setRaw] = useState([]);
  const sections = useMemo(() => raw.map((s) => localised(s, lang)), [raw, lang]);
  const [q, setQ] = useState("");
  const [meta, setMeta] = useState({ version: "", updated: "" });
  const { slug } = useParams();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/help/articles").then((r) => {
      setRaw(r.data.sections || []);
      setMeta({ version: r.data.version, updated: r.data.last_updated });
    }).catch(() => setRaw([]));
  }, []);

  const filtered = useMemo(() => {
    if (!q || q.length < 2) return sections;
    const nl = q.toLowerCase();
    const inBlocks = (blocks) => blocks?.some((b) => {
      if (b.text) return String(b.text).toLowerCase().includes(nl);
      if (b.items) return b.items.some((i) => String(i).toLowerCase().includes(nl));
      if (b.header) return b.header.some((h) => String(h).toLowerCase().includes(nl))
        || b.rows?.some((r) => r.some((c) => String(c).toLowerCase().includes(nl)));
      return false;
    });
    return sections.filter((s) =>
      s.title.toLowerCase().includes(nl)
      || (s.subtitle || "").toLowerCase().includes(nl)
      || inBlocks(s.body)
    );
  }, [sections, q]);

  const current = useMemo(() => {
    if (slug) return sections.find((s) => s.slug === slug);
    return filtered[0] || sections[0];
  }, [slug, sections, filtered]);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]" data-testid="help-center">
      {/* Sidebar */}
      <aside className="rounded-lg border bg-white p-3 dark:bg-slate-900 dark:border-slate-800">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
          <BookOpen className="h-4 w-4 text-emerald-600" /> AZEX Help Center
        </div>
        <div className="relative mb-3">
          <Search className="pointer-events-none absolute start-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 className="h-8 ps-7 text-xs"
                 placeholder="Search articles…"
                 data-testid="help-search" />
        </div>
        <div className="max-h-[65vh] overflow-y-auto pe-1">
          {filtered.map((s) => (
            <button key={s.slug}
                    onClick={() => nav(`/help/${s.slug}`)}
                    data-testid={`help-nav-${s.slug}`}
                    className={`group mb-0.5 flex w-full items-center gap-2 rounded px-2 py-1.5 text-start text-xs transition
                      ${current?.slug === s.slug
                        ? "bg-emerald-600 text-white"
                        : "hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
              <IconFor name={s.icon} className={`h-3.5 w-3.5 ${current?.slug === s.slug ? "" : "text-emerald-500"}`} />
              <span className="min-w-0 flex-1 break-words" dir="auto">{s.id}. {s.title}</span>
              {current?.slug === s.slug && <ChevronRight className="h-3 w-3" />}
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="p-4 text-center text-xs text-muted-foreground">No articles match "{q}".</div>
          )}
        </div>

        <div className="mt-4 border-t pt-3 dark:border-slate-800">
          <div className="text-[10px] text-muted-foreground">
            v{meta.version} · {meta.updated}
          </div>
        </div>
      </aside>

      {/* Article */}
      <main className="rounded-lg border bg-white p-6 dark:bg-slate-900 dark:border-slate-800" data-testid="help-article">
        {current ? (
          <>
            <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
              <IconFor name={current.icon} className="h-3.5 w-3.5 text-emerald-500" />
              <span>Section {current.id} of {sections.length}</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100"
                data-testid="help-title" dir="auto">
              {current.title}
            </h1>
            {current.subtitle && (
              <p className="mt-1 mb-4 text-sm text-muted-foreground">{current.subtitle}</p>
            )}
            <div className="prose prose-slate max-w-none">
              {current.body?.map((b, i) => <Block key={i} b={b} q={q} />)}
            </div>
          </>
        ) : (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        )}
      </main>
    </div>
  );
}
